import { Box, Grid, Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IdDocumentType } from "../../api/graphql";
import StepperFormWrapper from "../../lib/StepperFormWrapper";
import { StepperType } from "../../lib/StepperWrapper";
import { ScreenTitle } from "../../lib/typography";
import UploadIDBack from "./UploadIDBack.svg";
import UploadIDFront from "./UploadIDFront.svg";
import UploadIDSelfie from "./UploadIDSelfie.svg";
import { UploadImageInputBase } from "./UploadImageInput";
import useLabelsForDocumentType from "./useLabelsForDocumentType";

export type UploadPhotosStepData = {
  idImageOne?: File;
  idImageTwo?: File;
  idImageThree?: File;
};

export type IdDocumentTypeWithoutBirthCertificate = Exclude<
  IdDocumentType,
  IdDocumentType.BirthCertificate
>;

type Props = {
  documentType: IdDocumentTypeWithoutBirthCertificate;
  defaultValues: Partial<UploadPhotosStepData>;
  onNext: (data: UploadPhotosStepData) => void;
  stepper: StepperType;
};

export default function UploadPhotosStep({
  documentType,
  defaultValues,
  onNext,
  stepper,
}: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<UploadPhotosStepData>(defaultValues);
  const labelsForDocumentType = useLabelsForDocumentType();

  // Moved inside the function to allow for correct translation
  const DOCUMENT_TYPE_LABELS: Record<
    IdDocumentTypeWithoutBirthCertificate,
    string
  > = {
    [IdDocumentType.Cnic]: t("CNIC"),
    [IdDocumentType.DriversLicense]: t("driver's license"),
    [IdDocumentType.IdCard]: t("ID card"),
    [IdDocumentType.Other]: t("document"),
    [IdDocumentType.Passport]: t("passport"),
    [IdDocumentType.Undocumented]: t("face"),
  };

  const isFormDisabled =
    documentType === IdDocumentType.Undocumented
      ? !data.idImageThree
      : !(data.idImageOne && data.idImageTwo && data.idImageThree);

  return (
    <StepperFormWrapper
      stepper={stepper({
        disabled: isFormDisabled,
      })}
      handleSubmit={(event) => {
        event?.preventDefault();
        onNext(data);
      }}
    >
      <ScreenTitle>
        {documentType === IdDocumentType.Undocumented
          ? t("Upload your photo")
          : t("Upload photos of your {{documentType}}", {
              documentType: DOCUMENT_TYPE_LABELS[documentType],
            })}
      </ScreenTitle>
      <Box
        sx={{
          my: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            color: "text.primary",
          }}
        >
          {labelsForDocumentType[documentType].header.description}
        </Typography>
      </Box>
      <Grid
        container
        sx={{
          mt: 4,
          mb: 11,
        }}
      >
        {documentType !== IdDocumentType.Undocumented && (
          <>
            <Grid size={{ xs: 12 }}>
              <UploadImageInputBase
                id="imageOne"
                title={labelsForDocumentType[documentType].imageOne.title}
                description={
                  labelsForDocumentType[documentType].imageOne.description
                }
                value={data.idImageOne}
                placeholderImage={{
                  src: UploadIDFront,
                  alt: t("front of an identification card"),
                }}
                onChange={(value) => {
                  setData({ ...data, idImageOne: value });
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <UploadImageInputBase
                id="imageTwo"
                title={labelsForDocumentType[documentType].imageTwo.title}
                description={
                  labelsForDocumentType[documentType].imageTwo.description
                }
                value={data.idImageTwo}
                placeholderImage={{
                  src: UploadIDBack,
                  alt: t("back of an identification card"),
                }}
                onChange={(value) => {
                  setData({ ...data, idImageTwo: value });
                }}
              />
            </Grid>
          </>
        )}
        <Grid size={{ xs: 12 }}>
          <UploadImageInputBase
            id="imageThree"
            title={labelsForDocumentType[documentType].imageThree.title}
            description={
              labelsForDocumentType[documentType].imageThree.description
            }
            value={data.idImageThree}
            placeholderImage={{
              src: UploadIDSelfie,
              alt: t(
                "person holding an identification card next to their face"
              ),
            }}
            onChange={(value) => {
              setData({ ...data, idImageThree: value });
            }}
            sx={{ borderBottom: 0 }}
          />
        </Grid>
      </Grid>
    </StepperFormWrapper>
  );
}
